import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subject, forkJoin, BehaviorSubject, EMPTY, of, Observable } from 'rxjs';
import { switchMap, catchError, tap, map, filter, startWith, shareReplay } from 'rxjs/operators';
import { environment } from '@lmc-env/environment';
import { formatDate } from '@angular/common';
import { firstValueFrom } from 'rxjs';

interface LMCBank {
  account_no: string;
  transit_number: string;
  institution_number: string;
  direct_deposit_name: string;
  institution_name: string;
  loan_group: string;
  loan_group_name: string;
}

@Injectable({ providedIn: 'root' })
export class LoanMetaService {
  uri: string = environment.DO_URL;
  private http = inject(HttpClient);

  // Navigation State
  private queueList: string[] = []; 
  private currentIndex = 0;  // <--- Ensure this exists here
  private isQueue: boolean = false;

  // State Management
  private readonly _loanState$ = new BehaviorSubject<any | null>(null);
  public readonly data$ = this._loanState$.asObservable();
  
  // Triggers
  private fetchTrigger = new BehaviorSubject<string | null>(null);
  private loanMetaAction$ = new Subject<{ isNote: boolean, actionType: string, data: any }>();

  constructor() {
    this.refreshQueue();
    this.initMasterStream(); // Handles full loads on next/prev/search
    this.initActionStream(); // Handles selective note reloads
  }

  // --- NAVIGATION METHODS ---
  isIndividualSearch(){
    return this.isQueue;
  }

  setQueue(ids: string[], startId: string, isQueue: boolean) {
    this.isQueue = isQueue;
    this.queueList = ids;
    const foundIndex = ids.indexOf(startId);
    this.saveSession(ids,foundIndex,startId,isQueue);
    this.currentIndex = foundIndex !== -1 ? foundIndex : 0;
    this.loadLoan(this.queueList[this.currentIndex]);
  }

  saveSession(ids: string[], queueIndex: number, startId: string, isQueue: boolean){    
    sessionStorage.setItem('nls_record', JSON.stringify({queueList:ids,currentIndex:queueIndex, currentLoan:startId,isQueue:isQueue}));
  }
  
  private refreshQueue() {
    const saved = sessionStorage.getItem('nls_record');
    if (saved){
        const parsedData = JSON.parse(saved);
        this.isQueue = parsedData?.isQueue
        this.queueList =parsedData?.queueList;
        this.currentIndex = parsedData?.currentIndex;
        this.loadLoan(this.queueList[this.currentIndex]);
    }
  }

  next() {
    if (this.currentIndex < this.queueList.length - 1) {
      this.currentIndex++;
      this.saveSession(this.queueList,this.currentIndex,this.queueList[this.currentIndex],true);
      this.loadLoan(this.queueList[this.currentIndex]);
    }
  }

  previous() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.saveSession(this.queueList,this.currentIndex,this.queueList[this.currentIndex],true);
      this.loadLoan(this.queueList[this.currentIndex]);
    }
  }

  // BUTTON LOGICS
  get canGoNext(): boolean {
    return this.isQueue && this.currentIndex < this.queueList.length - 1;
  }

  get canGoPrevious(): boolean {
    return this.isQueue && this.currentIndex > 0;
  }


  // PUBLIC TRIGGER METHODS
  loadLoan(id: string) {
    this.fetchTrigger.next(id);
  }

  // Data with type of reload
  triggerAction(isNote: boolean, actionType:string, newData: any) {
    this.loanMetaAction$.next({ isNote, actionType, data: newData });
  }

  // Define static streams with shareReplay so they only execute once for static data
  private staticLoanMetaData$ = forkJoin({
    underwritersData: this.http.get(`${this.uri}/getUnderwriters`).pipe(catchError(() => of([]))),
    buttonPermissionData: this.http.get(`${this.uri}/getBtnPermission`).pipe(catchError(() => of([]))),
  }).pipe(shareReplay(1));

  // MASTER STREAM (Full Load)
  private initMasterStream() {
    this.fetchTrigger.pipe(
      filter(id => !!id),
      switchMap(id => this.http.post(`${this.uri}/searchNLSLoan`, { searchData: id }).pipe(
        startWith(null), // Triggers UI loading state
        switchMap((response: any) => {
          if (!response) return of(null);
          const loan = Array.isArray(response) ? response[0] : response;
          if (!loan?.acctrefno) return of({ notFound: true });

          const currentPayOffDate = formatDate(new Date(), 'yyyy-MM-dd', 'en-US'); 
          return forkJoin({
            main: of(loan),
            // amoData: this.http.post(`${this.uri}/getNLSAmortizationSchedule`, {acctrefno: loan.acctrefno,	run_through_date: '2021-09-27'}).pipe(catchError(() => of([]))),
            noteData: this.http.post(`${this.uri}/getComments`, {info: loan.acctrefno}).pipe(catchError(() => of([]))),
            deferredData: this.http.post(`${this.uri}/getDeferredPayments`, {info: loan.acctrefno}).pipe(catchError(() => of([]))),
            loanDetailData: this.http.post(`${this.uri}/getLoandetails`, {info: loan.acctrefno}).pipe(catchError(() => of([]))),
            payOffData: this.http.post(`${this.uri}/getPayoffInfo`, {acctrefno: loan.acctrefno, payoff_date:currentPayOffDate}).pipe(catchError(() => of([]))),
            emergencyContact: this.http.post(`${this.uri}/getNLSemergencyContact`, {acctrefno: loan.acctrefno}).pipe(catchError(() => of([]))),
            coAppEmergencyContact: this.http.post(`${this.uri}/getNLSCoAppEmergencyContact`, {acctrefno: loan.acctrefno}).pipe(catchError(() => of([]))),
            static: this.staticLoanMetaData$
          }).pipe(
            // Map back to your original structure
            map(data => ({
              ...data,
              underwritersData: data.static.underwritersData,
              buttonPermissionData: data.static.buttonPermissionData
            }))
          );;
        })
      ))
    ).subscribe(data => this._loanState$.next(data));
  }

  // Selective action stream
  private initActionStream() {
    this.loanMetaAction$.pipe(
      tap(results => console.log('RESULT OF CREATE COMMENT:', results)),
      switchMap(({ isNote,actionType, data }) => {
        // Endpoint action
        let apiCall$: Observable<any>;

        if (actionType === 'RELOAD_ONLY') {
          // Skip the POST/PUT and just pass the ID forward
          apiCall$ = of({ isNote: isNote, data });
        } else {
          apiCall$ = this.http.post<any>(`${this.uri}/createComment`, data)
        }

        return apiCall$.pipe(
          tap(results => console.log('RESULT OF CREATE PTP:', results)),
          switchMap(newRecord => {
            const targetId = newRecord.acctrefno || data.acctrefno;
            const reload$ = isNote 
            ? this.http.post(`${this.uri}/getComments`, { info: targetId }).pipe(
                tap(results => console.log('RETURN NOTES:', results)),  
                map(notes => ({ noteData: notes })) // Wrap in object to match state structure
              )
            : forkJoin({
                noteData: this.http.post(`${this.uri}/getComments`, { info: targetId }).pipe(catchError(() => of([])))
              });
            
            // Selective Reload: Only fetch comments
            return reload$.pipe(
              tap(reloadedParts  => {
                const currentState = this._loanState$.getValue();
                if (currentState) {
                  // Merge: Update ONLY notes in the master state
                  this._loanState$.next({
                    ...currentState,
                    ...reloadedParts,
                    _lastUpdate: Date.now()
                  });
                }
              }),
              // Ensure we return the notes so the component 'next' fires
              map(reloadedParts  => ({ success: true, ...reloadedParts })) 
            );
          }),
          catchError(err => {
            console.error('Action failed', err);
            return EMPTY; 
          }) // Keep the button active if save fails
        );
      })
    ).subscribe();
  }

  /**
   * Update or insert legal status
   * @param acctrefno 
   * @param status_code_no 
   * @param requestType 
   * @returns 
   */
  updateLegalStatus(acctrefno:number, status_code_no:number, requestType:string):Observable<any> {
		return this.http.put(`${this.uri}/updateLegalStatus`, {acctrefno,status_code_no,requestType});
	}

  /**
   * 
   * @param loanGroupNo 
   * @returns Get the LMC Payable accounts
   */
  getPayableBankingAccount(loanGroupNo:number): Promise<LMCBank[]>  {
    return firstValueFrom(this.http.get<LMCBank[]>(`${this.uri}/payableBankingAccount/${loanGroupNo}`));
  }
}
